// packages/game/src/components/PlayerCardPill.ts
//
// Top-right pill that opens the player-card overlay. Mirrors
// BackToFloorPill's styling so the chrome at the top of the screen reads
// as a matched pair of corner controls.
import type Phaser from 'phaser';

export interface PlayerCardPillOptions {
  scene: Phaser.Scene;
  /** Right edge of the pill (canvas-buffer px). */
  rightX: number;
  /** Top edge of the pill (canvas-buffer px). */
  topY: number;
  /** Scale multiplier for px sizes (default 1). */
  scale?: number;
  onClick: () => void;
}

export class PlayerCardPill {
  private rect: Phaser.GameObjects.Rectangle;
  private text: Phaser.GameObjects.Text;
  private bg: Phaser.GameObjects.Graphics;

  constructor(opts: PlayerCardPillOptions) {
    const { scene, rightX, topY, onClick } = opts;
    const sm = opts.scale ?? 1;
    const padX = 9 * sm;
    const padY = 5 * sm;

    this.text = scene.add
      .text(0, 0, 'PLAYER CARD ▸', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${Math.round(11 * sm)}px`,
        color: '#d4af37',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    const w = this.text.width + padX * 2;
    const h = this.text.height + padY * 2;
    const x = rightX - w;
    const y = topY;

    this.text.setPosition(x + padX, y + padY);

    this.bg = scene.add.graphics();
    this.bg.fillStyle(0x06100c, 0.7);
    this.bg.fillRect(x, y, w, h);
    // Accent stripe on the RIGHT edge to mirror the FLOOR pill's left
    // stripe on the opposite corner.
    this.bg.fillStyle(0xd4af37, 1);
    this.bg.fillRect(x + w - 2 * sm, y, 2 * sm, h);
    this.bg.setDepth(-1);

    this.rect = scene.add
      .rectangle(x + w / 2, y + h / 2, w, h, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.rect.on('pointerdown', onClick);
    this.rect.on('pointerover', () => this.text.setColor('#f4d35e'));
    this.rect.on('pointerout', () => this.text.setColor('#d4af37'));
  }

  destroy() {
    this.rect.destroy();
    this.text.destroy();
    this.bg.destroy();
  }
}
